/**
 * KAPITÄN-RELEASE — Countdown & Zustandswechsel
 * ---------------------------------------------------------------------------
 * Keine Abhängigkeiten, ~3 kB. Zwei Custom Elements:
 *
 *   <release-countdown release-at="<unix-sekunden>">
 *     Zählt herunter. Feuert `release:live`, wenn 0 erreicht ist.
 *
 *   <release-state release-at="<unix-sekunden>" [product-handle="..."]>
 *     Hüllt eine Release-Komponente. Setzt data-state="pending" | "live".
 *     Bei `release:live` wechselt der Zustand sofort — auch wenn Shopify die
 *     Seite noch aus dem CDN-Cache ausgeliefert hat.
 *
 *     Mit `product-handle` prüft es zusätzlich, ob genau dieses Produkt im Shop
 *     schon erreichbar ist, und setzt data-available="true"|"false".
 *
 *     Die Elemente dürfen verschachtelt werden: Jede Teaser-Karte kapselt ihr
 *     eigenes Produkt, der umschließende Block sammelt nur ein. Meldet ein
 *     inneres Element Verfügbarkeit, erfährt der äußere Block das über das
 *     Event `release:available` und schaltet seinen eigenen CTA frei. Ohne das
 *     würden beide Karten gemeinsam freigeschaltet, sobald irgendein Produkt
 *     live ist — und die andere Karte zeigte auf eine 404-Seite.
 *
 * SICHERHEITSHINWEIS
 * Diese Datei ist reine PRÄSENTATION. Sie entscheidet nicht darüber, ob ein
 * Produkt gekauft werden kann. Die Kaufsperre liegt in Shopify selbst
 * (Bestand 0 + inventory_policy DENY) und greift unabhängig von diesem Code.
 * Siehe snippets/release-config.liquid und docs/KAPITAEN-RELEASE.md.
 *
 * TEST-OVERRIDE (nur Darstellung, hebt die Kaufsperre NICHT auf):
 *   ?release_preview=before   → weit vor dem Release
 *   ?release_preview=soon     → 10 Sekunden vor dem Release (Umschalten live sehen)
 *   ?release_preview=after    → nach dem Release
 */
(() => {
  'use strict';

  const PREVIEW = new URLSearchParams(location.search).get('release_preview');

  /* Einmal abgefragt, überall benutzt. Als MediaQueryList und nicht als
     Momentaufnahme, damit ein Umschalten im Betriebssystem sofort wirkt.
     Der Ersatzwert ist kein Zierrat: Diese Datei läuft in den Tests gegen ein
     sehr kleines Fake-DOM, und sie soll an einer reinen Deko-Abfrage nicht
     schon beim Laden scheitern — sonst nähme ein fehlendes matchMedia den
     ganzen Countdown mit. */
  const REDUCED_MOTION =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };

  /**
   * Serverzeit-Offset in Millisekunden (Serverzeit − Clientzeit).
   * Der Countdown läuft sofort mit der lokalen Uhr los und korrigiert sich,
   * sobald die Antwort da ist. Ohne das würde eine falsch gestellte Uhr des
   * Besuchers den Countdown verschieben.
   */
  let clockOffset = 0;

  function now() {
    return Date.now() + clockOffset;
  }

  // /cart.js wird von Shopify nie im CDN gecacht → der Date-Header ist echte Serverzeit.
  function syncClock() {
    if (PREVIEW) return Promise.resolve();
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      method: 'HEAD',
      cache: 'no-store',
    })
      .then((res) => {
        const header = res.headers.get('date');
        if (!header) return;
        const serverMs = Date.parse(header);
        if (!Number.isFinite(serverMs)) return;
        const delta = serverMs - Date.now();
        // Nur bei nennenswerter Abweichung korrigieren; der Date-Header hat
        // Sekundenauflösung, Mikrokorrekturen würden nur den Timer zappeln lassen.
        if (Math.abs(delta) > 2000) clockOffset = delta;
      })
      .catch(() => {
        /* Offline oder blockiert: lokale Uhr ist gut genug. */
      });
  }

  /** Effektiver Release-Zeitpunkt in ms, inkl. Test-Override. */
  function targetMs(attrSeconds) {
    const real = Number(attrSeconds) * 1000;
    switch (PREVIEW) {
      case 'before':
        return Date.now() + 1000 * 60 * 60 * 51; // gut zwei Tage
      case 'soon':
        return Date.now() + 1000 * 10;
      case 'after':
        return Date.now() - 1000 * 60 * 5;
      default:
        return real;
    }
  }

  function pad(value) {
    return value < 10 ? `0${value}` : String(value);
  }

  // ---------------------------------------------------------------------------
  // <release-countdown>
  // ---------------------------------------------------------------------------
  class ReleaseCountdown extends HTMLElement {
    connectedCallback() {
      this.target = targetMs(this.getAttribute('release-at'));
      this.fields = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.fired = false;

      this.tick();
      syncClock().then(() => this.tick());
      this.timer = setInterval(() => this.tick(), 1000);

      // Nach Ruhezustand/Tab-Wechsel kann das Intervall Sekunden verschluckt
      // haben — beim Zurückkommen sofort neu rechnen statt nachzuzählen.
      this.onVisible = () => {
        if (!document.hidden) this.tick();
      };
      document.addEventListener('visibilitychange', this.onVisible);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    tick() {
      // Nie negativ: bei 0 abschneiden.
      const remaining = Math.max(0, this.target - now());
      const totalSeconds = Math.floor(remaining / 1000);

      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.write('days', pad(days));
      this.write('hours', pad(hours));
      this.write('minutes', pad(minutes));
      this.write('seconds', pad(seconds));

      if (remaining <= 0 && !this.fired) {
        this.fired = true;
        clearInterval(this.timer);
        this.dispatchEvent(new CustomEvent('release:live', { bubbles: true }));
      }
    }

    write(unit, text) {
      const el = this.fields[unit];
      if (!el || el.textContent === text) return;
      el.textContent = text;

      // Nur die Sekunden bekommen den kleinen Anstoß. Bei allen vier Feldern
      // zugleich — einmal pro Minute, Stunde, Tag — sähe es aus, als hätte der
      // Countdown einen Schluckauf. Die Animation läuft ausschließlich in CSS;
      // hier wird nur das Attribut gesetzt und nach dem Lauf wieder entfernt,
      // damit sie beim nächsten Wechsel erneut startet.
      if (unit !== 'seconds' || REDUCED_MOTION.matches) return;
      el.removeAttribute('data-tick');
      // Ein erzwungener Reflow, sonst fasst der Browser Entfernen und Setzen
      // in einem Frame zusammen und die Animation startet nie neu.
      void el.offsetWidth;
      el.setAttribute('data-tick', '');
    }
  }

  // ---------------------------------------------------------------------------
  // <release-state>
  // ---------------------------------------------------------------------------
  class ReleaseState extends HTMLElement {
    connectedCallback() {
      this.target = targetMs(this.getAttribute('release-at'));
      this.dropped = false;
      this.wentLive = false;

      // Ein verschachteltes Element meldet nach oben, dass sein Produkt da ist.
      // Der äußere Block braucht das für seinen eigenen CTA — er hat kein
      // eigenes Handle und wüsste sonst nichts davon.
      this.onChildAvailable = (event) => {
        if (event.target === this) return;
        this.setAttribute('data-available', 'true');
      };
      this.addEventListener('release:available', this.onChildAvailable);

      if (now() >= this.target) {
        this.goLive();
      } else {
        this.setAttribute('data-state', 'pending');
        this.addEventListener('release:live', () => this.goLive());
        // Rückfallebene, falls kein <release-countdown> darin steckt.
        this.timer = setTimeout(() => this.goLive(), this.target - now());
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.retryTimer);
      this.removeEventListener('release:available', this.onChildAvailable);
      this.dropped = true;
    }

    goLive() {
      // Gegen doppelte Ausführung schützt ein eigenes Flag, NICHT das Attribut:
      // Nach dem Release-Zeitpunkt rendert Liquid data-state="live" schon in
      // den Quelltext. Prüfte die Guard das Attribut, kehrte sie bei jedem
      // frischen Seitenaufruf sofort zurück — und verifyAvailability() liefe
      // nie. Damit bliebe data-available ungesetzt, CSS zeigte den Standard
      // (Kauf-Weg offen), und die Teaser-Links wiesen auf Produkte, die noch
      // Entwurf sind. Genau der Normalfall ab 18:00 Uhr.
      if (this.wentLive) return;
      this.wentLive = true;

      clearTimeout(this.timer);
      this.setAttribute('data-state', 'live');
      this.verifyAvailability();
    }

    /**
     * Der Countdown läuft im Browser ab, das Produkt wird aber von Hand im
     * Shopify-Admin auf "Aktiv" gestellt. Zwischen beidem liegen im Zweifel
     * Minuten. Deshalb: echten Produktstatus nachfragen, statt einen Kauf-Weg
     * zu behaupten, den es noch nicht gibt — ein Klick auf "Jetzt ansehen"
     * würde sonst auf einer 404-Seite landen.
     *
     * data-available="true"  → Produktseite existiert und Shopify verkauft
     * data-available="false" → noch nicht da, Hinweis statt CTA
     *
     * ---------------------------------------------------------------------
     * Ohne eigenes `product-handle` gibt es zwei völlig verschiedene Fälle,
     * und sie zu verwechseln kostet den Kauf-Weg:
     *
     * 1. Der Block UMSCHLIESST Karten mit eigenem Handle (der große
     *    Kampagnenblock). Dann wartet er, bis eine davon `release:available`
     *    meldet — bis dahin zu Recht "noch nicht verfügbar", sonst schickte
     *    sein CTA die Leute in eine Collection, in der nichts liegt.
     *
     * 2. Der Block umschließt gar nichts Prüfbares (der schmale Erinnerer).
     *    Dann kann niemals jemand melden, dass etwas da ist — ein hartes
     *    "false" bliebe für immer stehen. Genau das ist passiert: Nach dem
     *    Drop hätte dort dauerhaft "Der Verkauf wird gerade freigeschaltet"
     *    gestanden und nie der Button. Für diesen Fall wird gar nicht
     *    gegated; ohne data-available zeigt das CSS den CTA, und das ist
     *    hier auch richtig, weil der CTA auf eine Collection zeigt, die es
     *    immer gibt.
     */
    verifyAvailability() {
      const handle = this.getAttribute('product-handle');
      if (!handle) {
        // Fall 2: Niemand kann je etwas melden → nicht gaten.
        if (!this.querySelector('release-state[product-handle]')) return;
        // Fall 1: Auf die Meldung einer Karte warten.
        this.setAttribute('data-available', 'false');
        return;
      }

      /** Nächster Versuch: erst im Sekundentakt, dann alle 30 s, max. ~30 min. */
      const retry = (attempt) => {
        if (this.dropped || attempt >= 60) return;
        this.retryTimer = setTimeout(() => check(attempt + 1), Math.min(30000, 3000 * attempt));
      };

      const check = (attempt) => {
        if (this.dropped) return;
        fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
          cache: 'no-store',
        })
          .then((res) => {
            // 404 heißt hier nicht "Fehler", sondern "Produkt ist noch ein
            // Entwurf" — eine klare Aussage, kein Netzproblem.
            if (res.status === 404) return { available: false };
            // Alles andere ohne verwertbare Antwort (429 Drosselung, 500 …)
            // ist ein Aussetzer, keine Aussage über das Produkt.
            return res.ok ? res.json() : null;
          })
          .then((data) => {
            if (this.dropped) return;
            if (!data) {
              // Keine verwertbare Antwort — nachfassen statt stillschweigend
              // aufgeben. Bei Andrang antwortet Shopify mit 429, und genau
              // dann wird die Seite am meisten aufgerufen.
              retry(attempt);
              return;
            }
            // Einmal "verfügbar" bleibt verfügbar — ein inneres Element hat
            // vielleicht schon gemeldet, und ein späteres "false" von hier
            // würde einen funktionierenden Kauf-Weg wieder zunageln.
            if (this.getAttribute('data-available') === 'true') return;

            this.setAttribute('data-available', data.available ? 'true' : 'false');
            if (data.available) {
              this.dispatchEvent(new CustomEvent('release:available', { bubbles: true }));
            }
            // Bis zu einer halben Stunde nachfassen: Die Freischaltung passiert
            // von Hand und kann sich verzögern. Erst schnell, dann alle 30 s.
            if (!data.available) retry(attempt);
          })
          .catch(() => {
            // Netzfehler oder Drosselung (Shopify antwortet bei Andrang mit 429).
            // Den Zustand unverändert lassen, aber weiter nachfassen: Gäben wir
            // hier auf, bliebe data-available für immer ungesetzt — und ein
            // Teaser-Link zeigte auf ein Produkt, das vielleicht noch gar nicht
            // da ist. Ein einzelner Aussetzer um 18:00 darf das nicht auslösen.
            retry(attempt);
          });
      };

      check(1);
    }
  }

  if (!customElements.get('release-countdown')) {
    customElements.define('release-countdown', ReleaseCountdown);
  }
  if (!customElements.get('release-state')) {
    customElements.define('release-state', ReleaseState);
  }

  // ---------------------------------------------------------------------------
  // Einblenden beim Scrollen
  // ---------------------------------------------------------------------------
  /*
   * Elemente mit .js-reveal starten unsichtbar und fahren beim Hereinscrollen
   * ein. Zwei Dinge sind daran wichtiger, als sie aussehen:
   *
   * 1. Den Startzustand setzt CSS erst, wenn html die Klasse .js-reveal-ready
   *    trägt — und die wird hier vergeben. Lädt das Skript nicht, stünde der
   *    Startzustand sonst für immer, und die halbe Kampagne wäre unsichtbar.
   *
   * 2. Der Beobachter meldet jedes Element genau einmal und hängt sich danach
   *    ab. Ein Reveal, das beim Zurückscrollen wieder verschwindet, ist auf
   *    einer Verkaufsseite ein Ärgernis, kein Effekt.
   *
   * IntersectionObserver statt Scroll-Handler: kein Listener, der bei jedem
   * Pixel feuert, und der Browser rechnet das ohnehin schon aus.
   */
  function setupReveal() {
    const elemente = document.querySelectorAll('.js-reveal');
    if (!elemente.length) return;

    // Ohne Beobachter (sehr alte Browser) oder ohne gewünschte Bewegung:
    // alles sofort sichtbar, ohne Übergang.
    if (!('IntersectionObserver' in window) || REDUCED_MOTION.matches) {
      elemente.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    document.documentElement.classList.add('js-reveal-ready');

    const beobachter = new IntersectionObserver(
      (eintraege, self) => {
        for (const eintrag of eintraege) {
          if (!eintrag.isIntersecting) continue;
          eintrag.target.classList.add('is-revealed');
          self.unobserve(eintrag.target);
        }
      },
      // Etwas früher auslösen, als das Element wirklich zu sehen ist: Sonst
      // beginnt die Bewegung erst, wenn man schon hinschaut, und wirkt träge.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    elemente.forEach((el) => beobachter.observe(el));

    // Sicherheitsnetz: Was nach zwei Sekunden noch nicht eingeblendet ist —
    // etwa weil der Beobachter in einem versteckten Container hängt — wird
    // ungefragt sichtbar gemacht. Lieber ohne Animation als gar nicht.
    setTimeout(() => {
      document.querySelectorAll('.js-reveal:not(.is-revealed)').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-revealed');
      });
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReveal, { once: true });
  } else {
    setupReveal();
  }
})();
